/**
 * Bridge route for Data Science Lab sub-project deep-link sharing.
 *
 * When a sub-project (e.g. points-risk-control) detects an unauthenticated
 * request, it 302s the browser to:
 *     /ai/auth/data-science-lab-redirect?project=<slug>&return=<original path>
 *
 * Behavior:
 *   - If the user is already logged in to DC, fetch a one-time SSO URL for
 *     that sub-project and window.location.replace to it.
 *   - If not logged in, navigate to /login?next=<bridge URL> so they come
 *     back here after authenticating.
 *
 * Must NOT be wrapped in <ProtectedRoute>: the bridge needs to preserve the
 * project + return params when bouncing the user through /login.
 *
 * See docs/guides/data-science-lab-mounting-guide.md.
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useAuthStore from '../../../../stores/authStore';
import { authenticatedFetch } from '../../../../utils/apiClient';

export default function DataScienceLabRedirect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const project = params.get('project') ?? '';
  const returnPath = params.get('return') ?? '/';

  const isInitialized = useAuthStore(s => s.isInitialized);
  const accessToken = useAuthStore(s => s.accessToken);

  useEffect(() => {
    if (!isInitialized) return;

    if (!project) {
      console.error('[data-science-lab-redirect] missing project param');
      navigate('/', { replace: true });
      return;
    }

    if (!accessToken) {
      const here = `/auth/data-science-lab-redirect?project=${encodeURIComponent(
        project
      )}&return=${encodeURIComponent(returnPath)}`;
      navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(
          `/api/data-science-lab/sso-link?project=${encodeURIComponent(
            project
          )}&return_path=${encodeURIComponent(returnPath)}`
        );
        if (cancelled) return;

        if (!response.ok) {
          // 401 handled inside authenticatedFetch.
          // 403 (no data-science-lab permission), 404 (unknown slug), 503
          // (sub-project env not configured) land here.
          console.error(
            '[data-science-lab-redirect] sso-link failed:',
            response.status
          );
          navigate('/', { replace: true });
          return;
        }
        const data = await response.json();
        if (data?.url) {
          window.location.replace(data.url);
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[data-science-lab-redirect] error:', err);
          navigate('/', { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isInitialized, accessToken, project, returnPath, navigate]);

  return (
    <div
      style={{
        padding: 48,
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {isInitialized ? `Redirecting to ${project || 'sub-project'}…` : 'Loading…'}
    </div>
  );
}
